/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Rigid intramolecular constraints for the crystal MD engine, solved
 * with SHAKE (positions) and RATTLE (velocities) inside the
 * velocity-Verlet step.
 *
 * Why this exists: a flexible water model — harmonic O-H bonds plus
 * point charges — is fundamentally unstable. With only a spring holding
 * a hydrogen to its oxygen, the Coulomb attraction to a neighboring
 * molecule's charge sites pulls the hydrogen off and the structure
 * collapses (confirmed: an NVE run showed the potential energy crashing
 * ~8 eV/molecule). Every production water model — SPC, TIP3P,
 * TIP4P/Ice — is therefore RIGID. Rigidity is not an approximation
 * bolted on; it IS the model, and SHAKE/RATTLE is the standard way to
 * integrate a rigid molecule inside an otherwise ordinary
 * velocity-Verlet step.
 *
 * A 3-atom water is made rigid by three distance constraints — the two
 * O-H plus the H-H — removing exactly the 3 internal DOF and leaving
 * 3 translational + 3 rotational. The solver is generic over a list of
 * pairwise distance constraints, so the same code rigidifies any small
 * molecule once its constraint set is built.
 */
import { Vector3 } from 'three';
import { Atom } from '../Atom.ts';

/** One holonomic distance constraint |r_i − r_j| = √d2 between two atoms. */
export interface DistanceConstraint {
  /** Index into the dynamics' atom array. */
  i: number;
  j: number;
  /** Target squared distance (Å²). */
  d2: number;
}

export class ConstraintSet {
  constraints: DistanceConstraint[] = [];
  /** Hard cap on SHAKE / RATTLE sweeps — a rigid water converges in ~5. */
  maxIterations = 150;
  /** Relative convergence tolerance on the squared-distance residual. */
  tolerance = 1e-8;

  get length(): number {
    return this.constraints.length;
  }

  add(i: number, j: number, distance: number): void {
    this.constraints.push({ i, j, d2: distance * distance });
  }

  /**
   * SHAKE — restore every constrained distance after the unconstrained
   * position drift. The correction for a constraint is applied along
   * the bond vector from BEFORE the drift (`prevPositions`); using the
   * old direction is what makes SHAKE a valid projection back onto the
   * constraint manifold. Each position correction is mirrored into the
   * velocity (÷ dt) so the constrained trajectory stays consistent —
   * RATTLE then cleans up the velocity component exactly.
   */
  shake(atoms: Atom[], prevPositions: Vector3[], dt: number): void {
    if (this.constraints.length === 0) return;
    const invDt = 1 / dt;
    const r = new Vector3();
    const ref = new Vector3();
    for (let iter = 0; iter < this.maxIterations; iter++) {
      let converged = true;
      for (const c of this.constraints) {
        const ai = atoms[c.i];
        const aj = atoms[c.j];
        const invMi = ai.fixed ? 0 : 1 / ai.mass;
        const invMj = aj.fixed ? 0 : 1 / aj.mass;
        const invMassSum = invMi + invMj;
        if (invMassSum === 0) continue;
        r.subVectors(ai.position, aj.position);
        const diff = r.lengthSq() - c.d2;
        if (Math.abs(diff) > this.tolerance * c.d2) {
          converged = false;
          ref.subVectors(prevPositions[c.i], prevPositions[c.j]);
          const denom = 2 * r.dot(ref) * invMassSum;
          if (Math.abs(denom) < 1e-12) continue;
          const g = diff / denom;
          ai.position.addScaledVector(ref, -g * invMi);
          aj.position.addScaledVector(ref, g * invMj);
          ai.velocity.addScaledVector(ref, -g * invMi * invDt);
          aj.velocity.addScaledVector(ref, g * invMj * invDt);
        }
      }
      if (converged) return;
    }
  }

  /**
   * RATTLE — project the relative velocity along each constraint to
   * zero. After the second velocity half-kick the constraint distances
   * are already correct (SHAKE handled positions), but the velocities
   * may have a component along the bond; the constraint d/dt is
   * r_ij · v_ij = 0, enforced here.
   */
  rattle(atoms: Atom[]): void {
    if (this.constraints.length === 0) return;
    const r = new Vector3();
    const dv = new Vector3();
    for (let iter = 0; iter < this.maxIterations; iter++) {
      let converged = true;
      for (const c of this.constraints) {
        const ai = atoms[c.i];
        const aj = atoms[c.j];
        const invMi = ai.fixed ? 0 : 1 / ai.mass;
        const invMj = aj.fixed ? 0 : 1 / aj.mass;
        const invMassSum = invMi + invMj;
        if (invMassSum === 0) continue;
        r.subVectors(ai.position, aj.position);
        dv.subVectors(ai.velocity, aj.velocity);
        const rv = r.dot(dv);
        if (Math.abs(rv) > this.tolerance) {
          converged = false;
          const k = -rv / (r.lengthSq() * invMassSum);
          ai.velocity.addScaledVector(r, k * invMi);
          aj.velocity.addScaledVector(r, -k * invMj);
        }
      }
      if (converged) return;
    }
  }
}

/**
 * Build the rigid-water constraint set from the radial-bond graph.
 * Each oxygen with exactly two bonded hydrogens contributes three
 * distance constraints (O-H₁, O-H₂, H₁-H₂) — the minimal rigid set for
 * a 3-atom molecule. Target distances are taken from the current
 * geometry, so each water is frozen at the shape it was loaded with.
 *
 * An oxygen with a different hydrogen count (OH⁻, H₃O⁺, or a detection
 * glitch) is skipped rather than constrained into a junk geometry.
 */
export const buildWaterConstraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hydrogensOfOxygen = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'O' && ej === 'H') {
      if (!hydrogensOfOxygen.has(i)) hydrogensOfOxygen.set(i, []);
      hydrogensOfOxygen.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'O') {
      if (!hydrogensOfOxygen.has(j)) hydrogensOfOxygen.set(j, []);
      hydrogensOfOxygen.get(j)!.push(i);
    }
  }
  for (const [oIdx, hIdxs] of hydrogensOfOxygen) {
    if (hIdxs.length !== 2) continue;
    const o = atoms[oIdx];
    const h1 = atoms[hIdxs[0]];
    const h2 = atoms[hIdxs[1]];
    cs.add(oIdx, hIdxs[0], o.position.distanceTo(h1.position));
    cs.add(oIdx, hIdxs[1], o.position.distanceTo(h2.position));
    cs.add(hIdxs[0], hIdxs[1], h1.position.distanceTo(h2.position));
  }
  return cs;
};

/**
 * Build rigid-CO₂ constraints from a radial-bond adjacency. Each
 * carbon with exactly two bonded oxygens contributes three distance
 * constraints (C-O₁, C-O₂, O₁-O₂) — the minimal rigid set for a
 * linear triatomic. The O-O constraint is what pins the O-C-O angle
 * at 180° (without it the two C-O bonds could flex apart, breaking
 * the molecule's symmetry).
 *
 * Target distances are taken from the current geometry (typically
 * 1.149 Å for C-O and 2.298 Å for O-O at EPM2 equilibrium), so each
 * CO₂ is frozen at the shape it was loaded with. Like buildWater-
 * Constraints, this skips malformed cases (a C with ≠ 2 O neighbors)
 * rather than producing junk constraints.
 */
/**
 * Build rigid-PH₃ constraints. Same shape as buildNH3Constraints
 * (3 P-H + 3 H-H = 6 constraints per pyramid) but matches against
 * P-H bonds instead of N-H. Could be merged with buildNH3Constraints
 * via a general "pyramid central element" helper; kept separate for
 * symmetry with the other species-specific builders.
 */
export const buildPH3Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfP = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'P' && ej === 'H') {
      if (!hsOfP.has(i)) hsOfP.set(i, []);
      hsOfP.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'P') {
      if (!hsOfP.has(j)) hsOfP.set(j, []);
      hsOfP.get(j)!.push(i);
    }
  }
  for (const [pIdx, hIdxs] of hsOfP) {
    if (hIdxs.length !== 3) continue;
    const p = atoms[pIdx];
    for (const hIdx of hIdxs) {
      cs.add(pIdx, hIdx, p.position.distanceTo(atoms[hIdx].position));
    }
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        cs.add(hIdxs[i], hIdxs[j], atoms[hIdxs[i]].position.distanceTo(atoms[hIdxs[j]].position));
      }
    }
  }
  return cs;
};

/**
 * Pyramidal AH₃ rigidity helper. Finds each central atom (matching
 * `central`) with exactly 3 bonded hydrogens and adds 6 constraints
 * (3 central-H bonds + 3 H-H 1-3 distances) per pyramid. Shared core
 * for AsH₃; NH₃ and PH₃ keep their own copies for backwards compat.
 */
const buildPyramidalAH3 = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
  central: string,
): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfCentral = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === central && ej === 'H') {
      if (!hsOfCentral.has(i)) hsOfCentral.set(i, []);
      hsOfCentral.get(i)!.push(j);
    } else if (ei === 'H' && ej === central) {
      if (!hsOfCentral.has(j)) hsOfCentral.set(j, []);
      hsOfCentral.get(j)!.push(i);
    }
  }
  for (const [cIdx, hIdxs] of hsOfCentral) {
    if (hIdxs.length !== 3) continue;
    const c = atoms[cIdx];
    for (const hIdx of hIdxs) {
      cs.add(cIdx, hIdx, c.position.distanceTo(atoms[hIdx].position));
    }
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        cs.add(hIdxs[i], hIdxs[j], atoms[hIdxs[i]].position.distanceTo(atoms[hIdxs[j]].position));
      }
    }
  }
  return cs;
};

/** AsH₃ rigid pyramid — 6 constraints per molecule. */
export const buildAsH3Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildPyramidalAH3(atoms, radialBonds, 'As');

/**
 * Build rigid-NH₃ constraints. Each nitrogen with exactly 3 bonded
 * hydrogens contributes 6 distance constraints — 3 N-H bonds plus
 * 3 H-H 1-3 distances — pinning the pyramidal geometry. 6 is exactly
 * the internal-DOF count for a 4-atom molecule (4·3 − 6 = 6), so the
 * constraints fully rigidify the pyramid with no redundancy.
 */
export const buildNH3Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfN = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'N' && ej === 'H') {
      if (!hsOfN.has(i)) hsOfN.set(i, []);
      hsOfN.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'N') {
      if (!hsOfN.has(j)) hsOfN.set(j, []);
      hsOfN.get(j)!.push(i);
    }
  }
  for (const [nIdx, hIdxs] of hsOfN) {
    if (hIdxs.length !== 3) continue;
    const n = atoms[nIdx];
    // 3 N-H bond constraints
    for (const hIdx of hIdxs) {
      cs.add(nIdx, hIdx, n.position.distanceTo(atoms[hIdx].position));
    }
    // 3 H-H 1-3 constraints
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        cs.add(hIdxs[i], hIdxs[j], atoms[hIdxs[i]].position.distanceTo(atoms[hIdxs[j]].position));
      }
    }
  }
  return cs;
};

/**
 * Build rigid-CBr₄ constraints. Same shape as buildCH4Constraints
 * (4 C-Br bonds + 6 Br-Br 1-3 distances = 10 constraints per
 * tetrahedron), just keyed on C-Br bonds instead of C-H. The
 * tetrahedral all-pair set is over-determined for 6 internal DOF;
 * SHAKE/RATTLE handles the redundancy.
 */
export const buildCBr4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const brsOfC = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'Br') {
      if (!brsOfC.has(i)) brsOfC.set(i, []);
      brsOfC.get(i)!.push(j);
    } else if (ei === 'Br' && ej === 'C') {
      if (!brsOfC.has(j)) brsOfC.set(j, []);
      brsOfC.get(j)!.push(i);
    }
  }
  for (const [cIdx, brIdxs] of brsOfC) {
    if (brIdxs.length !== 4) continue;
    const c = atoms[cIdx];
    for (const brIdx of brIdxs) {
      cs.add(cIdx, brIdx, c.position.distanceTo(atoms[brIdx].position));
    }
    for (let i = 0; i < 4; i++) {
      for (let j = i + 1; j < 4; j++) {
        cs.add(brIdxs[i], brIdxs[j], atoms[brIdxs[i]].position.distanceTo(atoms[brIdxs[j]].position));
      }
    }
  }
  return cs;
};

/**
 * Build rigid-CH₄ constraints. Each carbon with exactly 4 bonded
 * hydrogens contributes 10 distance constraints — 4 C-H bonds plus
 * 6 H-H 1-3 distances — pinning the tetrahedral geometry. 10 is
 * over-determined (12 atomic DOF − 6 trans+rot = 6 internal),
 * but SHAKE/RATTLE handles the redundancy.
 */
export const buildCH4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfC = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'H') {
      if (!hsOfC.has(i)) hsOfC.set(i, []);
      hsOfC.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'C') {
      if (!hsOfC.has(j)) hsOfC.set(j, []);
      hsOfC.get(j)!.push(i);
    }
  }
  for (const [cIdx, hIdxs] of hsOfC) {
    if (hIdxs.length !== 4) continue;
    const c = atoms[cIdx];
    // 4 C-H bond constraints
    for (const hIdx of hIdxs) {
      cs.add(cIdx, hIdx, c.position.distanceTo(atoms[hIdx].position));
    }
    // 6 H-H 1-3 constraints (every pair of hydrogens within the tetrahedron)
    for (let i = 0; i < 4; i++) {
      for (let j = i + 1; j < 4; j++) {
        cs.add(hIdxs[i], hIdxs[j], atoms[hIdxs[i]].position.distanceTo(atoms[hIdxs[j]].position));
      }
    }
  }
  return cs;
};

/**
 * Build rigid-N₂ constraints. Each radial bond between two N atoms
 * becomes a single distance constraint — that's all a diatomic needs
 * for rigidity (1 internal DOF, 1 constraint). Target distance pinned
 * to the loaded geometry (typically 1.098 Å for Etters N₂).
 */
export const buildN2Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  for (const rb of radialBonds) {
    if (rb.atom1.elementSymbol !== 'N' || rb.atom2.elementSymbol !== 'N') continue;
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    cs.add(i, j, rb.atom1.position.distanceTo(rb.atom2.position));
  }
  return cs;
};

/**
 * Diatomic-rigidity helper. Adds one constraint per radial bond
 * matching the (a, b) element pair, where order doesn't matter
 * (a === b for homonuclear diatomics like H₂). Used by H₂ and CO
 * builders below; N₂ keeps its own copy for backwards compatibility.
 */
const buildDiatomicByPair = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
  elA: string,
  elB: string,
): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  for (const rb of radialBonds) {
    const e1 = rb.atom1.elementSymbol;
    const e2 = rb.atom2.elementSymbol;
    const matches = (e1 === elA && e2 === elB) || (e1 === elB && e2 === elA);
    if (!matches) continue;
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    cs.add(i, j, rb.atom1.position.distanceTo(rb.atom2.position));
  }
  return cs;
};

/** Rigid H-H constraint per H₂ molecule (1 constraint each). */
export const buildH2Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildDiatomicByPair(atoms, radialBonds, 'H', 'H');

/** Rigid C-O constraint per CO molecule (1 constraint each). */
export const buildCOConstraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildDiatomicByPair(atoms, radialBonds, 'C', 'O');

/**
 * Tetrahedral AB₄ rigidity helper. Finds each `central` atom with
 * exactly 4 bonded `terminal` neighbors and adds 10 constraints
 * (4 central-terminal bonds + 6 terminal-terminal 1-3 distances).
 * Shared core for SiH₄ / GeH₄ — CH₄ and CBr₄ keep their own copies
 * for backwards compatibility with the existing FF wiring.
 */
const buildTetrahedralAB4 = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
  central: string,
  terminal: string,
): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const terminalsOfCentral = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === central && ej === terminal) {
      if (!terminalsOfCentral.has(i)) terminalsOfCentral.set(i, []);
      terminalsOfCentral.get(i)!.push(j);
    } else if (ei === terminal && ej === central) {
      if (!terminalsOfCentral.has(j)) terminalsOfCentral.set(j, []);
      terminalsOfCentral.get(j)!.push(i);
    }
  }
  for (const [cIdx, tIdxs] of terminalsOfCentral) {
    if (tIdxs.length !== 4) continue;
    const c = atoms[cIdx];
    for (const tIdx of tIdxs) {
      cs.add(cIdx, tIdx, c.position.distanceTo(atoms[tIdx].position));
    }
    for (let i = 0; i < 4; i++) {
      for (let j = i + 1; j < 4; j++) {
        cs.add(tIdxs[i], tIdxs[j], atoms[tIdxs[i]].position.distanceTo(atoms[tIdxs[j]].position));
      }
    }
  }
  return cs;
};

/** SiH₄ rigid tetrahedron — 10 constraints per molecule. */
export const buildSiH4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildTetrahedralAB4(atoms, radialBonds, 'Si', 'H');

/** CF₄ rigid tetrahedron — 10 constraints per molecule. */
export const buildCF4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildTetrahedralAB4(atoms, radialBonds, 'C', 'F');

/** CCl₄ rigid tetrahedron — 10 constraints per molecule. */
export const buildCCl4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildTetrahedralAB4(atoms, radialBonds, 'C', 'Cl');

/** GeH₄ rigid tetrahedron — 10 constraints per molecule. */
export const buildGeH4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet =>
  buildTetrahedralAB4(atoms, radialBonds, 'Ge', 'H');

/**
 * Build rigid-SF₆ constraints. Each sulfur with exactly 6 bonded
 * fluorines contributes 21 distance constraints — 6 S-F bonds plus
 * all 15 F-F pairs (12 cis at 90° + 3 trans at 180°), pinning the
 * octahedral geometry. 21 is over-determined for the 15 internal
 * DOF (7·3 − 6 = 15), but SHAKE/RATTLE handles the redundancy and
 * the over-determined set is the most natural shape-locking pattern.
 */
export const buildSF6Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const fsOfS = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'S' && ej === 'F') {
      if (!fsOfS.has(i)) fsOfS.set(i, []);
      fsOfS.get(i)!.push(j);
    } else if (ei === 'F' && ej === 'S') {
      if (!fsOfS.has(j)) fsOfS.set(j, []);
      fsOfS.get(j)!.push(i);
    }
  }
  for (const [sIdx, fIdxs] of fsOfS) {
    if (fIdxs.length !== 6) continue;
    const s = atoms[sIdx];
    // 6 S-F bond constraints
    for (const fIdx of fIdxs) {
      cs.add(sIdx, fIdx, s.position.distanceTo(atoms[fIdx].position));
    }
    // 15 F-F pair constraints (12 cis + 3 trans, all pinned)
    for (let i = 0; i < 6; i++) {
      for (let j = i + 1; j < 6; j++) {
        cs.add(fIdxs[i], fIdxs[j], atoms[fIdxs[i]].position.distanceTo(atoms[fIdxs[j]].position));
      }
    }
  }
  return cs;
};

/**
 * Build rigid-P₄ constraints. Each radial bond between two P atoms
 * becomes a single distance constraint; in a P₄ tetrahedron all
 * C(4, 2) = 6 atom pairs are bonded (every edge of the tetrahedron),
 * so the 6 P-P bonds from the bond detector exactly match the 6
 * internal DOF (4·3 − 6 = 6). No long-range pins needed.
 *
 * Cross-tetrahedron bonds would corrupt this — the WHITE_PHOSPHORUS
 * preset is laid out so the closest inter-tetrahedron P···P (~3.80 Å)
 * comfortably exceeds the P bonding cutoff (~2.53 Å), so the bond
 * detector picks up only intra-tetrahedron bonds. If a preset
 * shrunk the lattice past that margin we would start over-binding.
 */
export const buildP4Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  for (const rb of radialBonds) {
    if (rb.atom1.elementSymbol !== 'P' || rb.atom2.elementSymbol !== 'P') continue;
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    cs.add(i, j, rb.atom1.position.distanceTo(rb.atom2.position));
  }
  return cs;
};

/**
 * Build rigid-CS₂ constraints. Mirror of buildCO2Constraints with S
 * in place of O: each carbon with exactly two bonded sulfurs gets
 * 3 distance constraints (C-S₁, C-S₂, S₁-S₂) — the S-S pin enforces
 * the linear S-C-S = 180° geometry.
 */
export const buildCS2Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const sulfursOfC = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'S') {
      if (!sulfursOfC.has(i)) sulfursOfC.set(i, []);
      sulfursOfC.get(i)!.push(j);
    } else if (ei === 'S' && ej === 'C') {
      if (!sulfursOfC.has(j)) sulfursOfC.set(j, []);
      sulfursOfC.get(j)!.push(i);
    }
  }
  for (const [cIdx, sIdxs] of sulfursOfC) {
    if (sIdxs.length !== 2) continue;
    const c = atoms[cIdx];
    const s1 = atoms[sIdxs[0]];
    const s2 = atoms[sIdxs[1]];
    cs.add(cIdx, sIdxs[0], c.position.distanceTo(s1.position));
    cs.add(cIdx, sIdxs[1], c.position.distanceTo(s2.position));
    cs.add(sIdxs[0], sIdxs[1], s1.position.distanceTo(s2.position));
  }
  return cs;
};

/**
 * Build rigid-acetylene (C₂H₂) constraints. Finds each C-C bond
 * between two carbons that each have exactly one bonded H, and adds
 * all 6 pairwise distance constraints across the 4 atoms (3 bonds
 * plus 3 long-range pins). The long-range pins are not in the
 * radial-bond graph (they exceed the bond detector's cutoff) so we
 * compute them from the loaded geometry, locking the molecule in
 * its rigid linear shape.
 */
export const buildAcetyleneConstraints = (
  atoms: Atom[],
  radialBonds: { atom1: Atom; atom2: Atom }[],
): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfC = new Map<number, number[]>();
  const ccBonds: Array<[number, number]> = [];
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'H') {
      if (!hsOfC.has(i)) hsOfC.set(i, []);
      hsOfC.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'C') {
      if (!hsOfC.has(j)) hsOfC.set(j, []);
      hsOfC.get(j)!.push(i);
    } else if (ei === 'C' && ej === 'C') {
      ccBonds.push([i, j]);
    }
  }
  for (const [c1, c2] of ccBonds) {
    const h1s = hsOfC.get(c1) ?? [];
    const h2s = hsOfC.get(c2) ?? [];
    // Acetylene: each C bonded to exactly one H. Reject any other
    // C-C bonded environment (ethane, ethylene, polymers) — those
    // are not acetylene molecules and shouldn't be rigidified here.
    if (h1s.length !== 1 || h2s.length !== 1) continue;
    const h1 = h1s[0];
    const h2 = h2s[0];
    // All 6 pairwise distances over the 4 collinear atoms: locks
    // both bond lengths AND the 180° geometry (the H₁-H₂ pin sets
    // the total length; the H₁-C₂ and C₁-H₂ pins force collinearity).
    const a1 = atoms[h1];
    const aC1 = atoms[c1];
    const aC2 = atoms[c2];
    const a2 = atoms[h2];
    cs.add(h1, c1, a1.position.distanceTo(aC1.position));
    cs.add(c1, c2, aC1.position.distanceTo(aC2.position));
    cs.add(c2, h2, aC2.position.distanceTo(a2.position));
    cs.add(h1, c2, a1.position.distanceTo(aC2.position));
    cs.add(c1, h2, aC1.position.distanceTo(a2.position));
    cs.add(h1, h2, a1.position.distanceTo(a2.position));
  }
  return cs;
};

/**
 * Build rigid-HCN constraints. Linear asymmetric H-C≡N — for each C
 * with exactly 1 H neighbor AND 1 N neighbor, add 3 distance
 * constraints (H-C, C-N bonds plus H-N long-range pin). The H-N pin
 * is past the bond detector's cutoff (~2.22 Å) so we compute it from
 * the loaded geometry. Three constraints suffice for the 3-atom
 * linear molecule (matches N₂O's count).
 */
export const buildHCNConstraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const hsOfC = new Map<number, number[]>();
  const nsOfC = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'H') {
      if (!hsOfC.has(i)) hsOfC.set(i, []);
      hsOfC.get(i)!.push(j);
    } else if (ei === 'H' && ej === 'C') {
      if (!hsOfC.has(j)) hsOfC.set(j, []);
      hsOfC.get(j)!.push(i);
    } else if (ei === 'C' && ej === 'N') {
      if (!nsOfC.has(i)) nsOfC.set(i, []);
      nsOfC.get(i)!.push(j);
    } else if (ei === 'N' && ej === 'C') {
      if (!nsOfC.has(j)) nsOfC.set(j, []);
      nsOfC.get(j)!.push(i);
    }
  }
  for (let cIdx = 0; cIdx < atoms.length; cIdx++) {
    if (atoms[cIdx].elementSymbol !== 'C') continue;
    const hs = hsOfC.get(cIdx) ?? [];
    const ns = nsOfC.get(cIdx) ?? [];
    if (hs.length !== 1 || ns.length !== 1) continue;
    const hIdx = hs[0];
    const nIdx = ns[0];
    const c = atoms[cIdx];
    cs.add(hIdx, cIdx, atoms[hIdx].position.distanceTo(c.position));
    cs.add(cIdx, nIdx, c.position.distanceTo(atoms[nIdx].position));
    // Linear pin (past bond cutoff): freezes 180° angle.
    cs.add(hIdx, nIdx, atoms[hIdx].position.distanceTo(atoms[nIdx].position));
  }
  return cs;
};

/**
 * Build rigid-cyanogen (NCCN) constraints. Same shape as acetylene
 * (linear 4-atom with central A-A bond and two terminals) — find
 * each C-C bond between two carbons that each have exactly one
 * bonded N neighbor, and add all 6 pairwise distance constraints
 * across the 4 atoms (3 bonds + 3 long-range pins).
 */
export const buildCyanogenConstraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const nsOfC = new Map<number, number[]>();
  const ccBonds: Array<[number, number]> = [];
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'N') {
      if (!nsOfC.has(i)) nsOfC.set(i, []);
      nsOfC.get(i)!.push(j);
    } else if (ei === 'N' && ej === 'C') {
      if (!nsOfC.has(j)) nsOfC.set(j, []);
      nsOfC.get(j)!.push(i);
    } else if (ei === 'C' && ej === 'C') {
      ccBonds.push([i, j]);
    }
  }
  for (const [c1, c2] of ccBonds) {
    const n1s = nsOfC.get(c1) ?? [];
    const n2s = nsOfC.get(c2) ?? [];
    // Cyanogen: each C has exactly one N neighbor. Reject any other
    // C-C environment (acetonitrile, polymeric chains).
    if (n1s.length !== 1 || n2s.length !== 1) continue;
    const n1 = n1s[0];
    const n2 = n2s[0];
    const a1 = atoms[n1];
    const aC1 = atoms[c1];
    const aC2 = atoms[c2];
    const a2 = atoms[n2];
    cs.add(n1, c1, a1.position.distanceTo(aC1.position));
    cs.add(c1, c2, aC1.position.distanceTo(aC2.position));
    cs.add(c2, n2, aC2.position.distanceTo(a2.position));
    cs.add(n1, c2, a1.position.distanceTo(aC2.position));
    cs.add(c1, n2, aC1.position.distanceTo(a2.position));
    cs.add(n1, n2, a1.position.distanceTo(a2.position));
  }
  return cs;
};

/**
 * Build rigid-N₂O constraints. Asymmetric linear N≡N-O — the central
 * N is the only atom bonded to BOTH another N AND an O. For each such
 * central N we add three distance constraints:
 *   - N(central)-N(terminal)  ≈ 1.128 Å (N≡N bond)
 *   - N(central)-O            ≈ 1.184 Å (N-O bond)
 *   - N(terminal)-O           ≈ 2.312 Å (linear pin; not a bond)
 * The N(terminal)-O distance is NOT in the radialBonds list (it's
 * past the bond-detector cutoff), so we compute it from the loaded
 * geometry and add it explicitly. Three constraints fully rigidify a
 * linear triatomic (4 internal DOF − 1 redundant = 3 independent).
 */
export const buildN2OConstraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  // For each N, collect its N-neighbors and O-neighbors.
  const nNbrsOfN = new Map<number, number[]>();
  const oNbrsOfN = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'N' && ej === 'N') {
      if (!nNbrsOfN.has(i)) nNbrsOfN.set(i, []);
      if (!nNbrsOfN.has(j)) nNbrsOfN.set(j, []);
      nNbrsOfN.get(i)!.push(j);
      nNbrsOfN.get(j)!.push(i);
    } else if (ei === 'N' && ej === 'O') {
      if (!oNbrsOfN.has(i)) oNbrsOfN.set(i, []);
      oNbrsOfN.get(i)!.push(j);
    } else if (ei === 'O' && ej === 'N') {
      if (!oNbrsOfN.has(j)) oNbrsOfN.set(j, []);
      oNbrsOfN.get(j)!.push(i);
    }
  }
  // Central N: has exactly 1 N neighbor AND exactly 1 O neighbor.
  for (let nIdx = 0; nIdx < atoms.length; nIdx++) {
    if (atoms[nIdx].elementSymbol !== 'N') continue;
    const ns = nNbrsOfN.get(nIdx) ?? [];
    const os = oNbrsOfN.get(nIdx) ?? [];
    if (ns.length !== 1 || os.length !== 1) continue;
    const nTerm = ns[0];
    const oIdx = os[0];
    const center = atoms[nIdx];
    cs.add(nIdx, nTerm, center.position.distanceTo(atoms[nTerm].position));
    cs.add(nIdx, oIdx, center.position.distanceTo(atoms[oIdx].position));
    // Linear pin: N(terminal)-O is past the bond cutoff but a rigid
    // constraint here freezes the molecule's 180° angle.
    cs.add(nTerm, oIdx, atoms[nTerm].position.distanceTo(atoms[oIdx].position));
  }
  return cs;
};

export const buildCO2Constraints = (atoms: Atom[], radialBonds: { atom1: Atom; atom2: Atom }[]): ConstraintSet => {
  const cs = new ConstraintSet();
  const indexOf = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) indexOf.set(atoms[i], i);
  const oxygensOfCarbon = new Map<number, number[]>();
  for (const rb of radialBonds) {
    const i = indexOf.get(rb.atom1);
    const j = indexOf.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    const ei = atoms[i].elementSymbol;
    const ej = atoms[j].elementSymbol;
    if (ei === 'C' && ej === 'O') {
      if (!oxygensOfCarbon.has(i)) oxygensOfCarbon.set(i, []);
      oxygensOfCarbon.get(i)!.push(j);
    } else if (ei === 'O' && ej === 'C') {
      if (!oxygensOfCarbon.has(j)) oxygensOfCarbon.set(j, []);
      oxygensOfCarbon.get(j)!.push(i);
    }
  }
  for (const [cIdx, oIdxs] of oxygensOfCarbon) {
    if (oIdxs.length !== 2) continue;
    const c = atoms[cIdx];
    const o1 = atoms[oIdxs[0]];
    const o2 = atoms[oIdxs[1]];
    cs.add(cIdx, oIdxs[0], c.position.distanceTo(o1.position));
    cs.add(cIdx, oIdxs[1], c.position.distanceTo(o2.position));
    cs.add(oIdxs[0], oIdxs[1], o1.position.distanceTo(o2.position));
  }
  return cs;
};
